import { uuid } from "../core/identity"
import type { NativePlatformInfo, StorageLike } from "./types"

const VISITOR_KEY = "relia.visitor_id"

/** Never `import "react-native"` at module scope — Metro optional peer, vitest/node lack it. */
export function getReactNative(): any | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const req = new Function("m", "try { return require(m) } catch (e) { return null }") as (
      m: string,
    ) => any
    return req("react-native")
  } catch {
    return null
  }
}

export function getAsyncStorage(): StorageLike | null {
  try {
    const req = new Function("m", "try { return require(m) } catch (e) { return null }") as (
      m: string,
    ) => any
    const mod = req("@react-native-async-storage/async-storage")
    const store = mod && (mod.default || mod)
    if (store && typeof store.getItem === "function" && typeof store.setItem === "function") {
      return store as StorageLike
    }
    return null
  } catch {
    return null
  }
}

export function getPlatformInfo(rn?: any): NativePlatformInfo {
  const platform = rn && rn.Platform ? rn.Platform : null
  const os = platform && typeof platform.OS === "string" ? platform.OS : "unknown"
  const version = platform && platform.Version != null ? String(platform.Version) : ""
  const isPad = Boolean(platform && (platform.isPad || platform.isTVOS))
  return { os, osVersion: version, model: isPad ? "Tablet" : "Phone", isTablet: isPad }
}

export function detectNativeDevice(info: Pick<NativePlatformInfo, "isTablet">): string {
  return info.isTablet ? "tablet" : "mobile"
}

export function buildUserAgent(info: NativePlatformInfo, appName: string, appVersion: string): string {
  const app = appName ? `; ${appName}${appVersion ? `/${appVersion}` : ""}` : ""
  return `ReliaRN/1.0 (${info.os} ${info.osVersion}; ${info.model}${app})`
}

export function getScreenSize(dims: { width: number; height: number } | null): string {
  if (!dims || !dims.width || !dims.height) return ""
  return `${Math.round(dims.width)}x${Math.round(dims.height)}`
}

export function getWindowDims(rn?: any): { width: number; height: number } | null {
  try {
    const d = rn && rn.Dimensions ? rn.Dimensions.get("window") : null
    if (d && typeof d.width === "number") return { width: d.width, height: d.height }
  } catch {
    // Dimensions unavailable (tests, web) — caller gets ""
  }
  return null
}

export async function resolveVisitorId(storage: StorageLike | null): Promise<string> {
  if (!storage) return uuid()
  try {
    const existing = await storage.getItem(VISITOR_KEY)
    if (existing) return existing
    const id = uuid()
    await storage.setItem(VISITOR_KEY, id)
    return id
  } catch {
    return uuid()
  }
}
